import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  ShieldCheck, 
  Clock, 
  Leaf, 
  MapPin, 
  FileCheck, 
  ArrowRight, 
  Phone, 
  Mail, 
  Building2, 
  Home as HomeIcon,
  Layers,
  Sparkles,
  Ruler,
  ShoppingBag,
  Wrench,
  HelpCircle,
  TrendingUp,
  Compass
} from 'lucide-react';
import Layout from '../components/Layout';
import ContactForm from '../components/ContactForm';
import FAQSection from '../components/FAQSection';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Jordskruer() {
  const heroImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/skruefundament-byggeplads-montering.jpg", { width: 1400, quality: 85 });
  const screwImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/jordskruer-teknisk-detalje.jpg", { width: 800, quality: 85 });
  const siteImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg", { width: 800, quality: 85 });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Jordskruer til fundament - CM Skruefundament ApS",
    "description": "Professionelle jordskruer i varmgalvaniseret stål fra WSF Schroeffundering til stabilt fundament uden omfattende gravearbejde.",
    "brand": {
      "@type": "Brand",
      "name": "WSF Schroeffundering"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "DKK",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "CM Skruefundament ApS",
        "url": "https://cmskruefundament.dk"
      }
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Jordskruer til et stærkt fundament uden gravearbejde | Ring nu</title>
        <meta 
          name="description" 
          content="Jordskruer er en effektiv løsning til stabilt fundament med begrænset gravearbejde. Køb jordskruer eller få hjælp til vurdering og dimensionering." 
        />
        <link rel="canonical" href="https://cmskruefundament.dk/jordskruer" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cmskruefundament.dk/jordskruer" />
        <meta property="og:title" content="Jordskruer til et stærkt fundament uden gravearbejde | Ring nu" />
        <meta 
          property="og:description" 
          content="Jordskruer er en effektiv løsning til stabilt fundament med begrænset gravearbejde. Køb jordskruer eller få hjælp til vurdering og dimensionering." 
        />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/1579e6f7-9ff1-4e95-aba1-981ac1255ac4.jpg" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="CM Skruefundament ApS" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cmskruefundament.dk/jordskruer" />
        <meta name="twitter:title" content="Jordskruer til et stærkt fundament uden gravearbejde | Ring nu" />
        <meta 
          name="twitter:description" 
          content="Jordskruer er en effektiv løsning til stabilt fundament med begrænset gravearbejde. Køb jordskruer eller få hjælp til vurdering og dimensionering." 
        />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative bg-slate-900 text-white pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Professionel montering af jordskruer på byggeplads med specialmaskine"
            className="w-full h-full object-cover opacity-20 filter brightness-75 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/90 to-slate-900/60" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs sm:text-sm font-bold uppercase tracking-widest mb-6">
                <Sparkles className="w-4 h-4" />
                Kvalitetsskruer i varmgalvaniseret stål
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl min-[992px]:text-[2.75rem] font-black uppercase tracking-tight text-white leading-tight mb-6">
                Jordskruer til et stærkt fundament uden omfattende gravearbejde
              </h1>

              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-light mb-8">
                Jordskruer er en effektiv løsning, når du vil skabe et stabilt fundament med begrænset gravearbejde. Hos <Link to="/" className="font-bold text-white underline decoration-amber-400 hover:text-amber-300 transition-colors">CM Skruefundament</Link> kan du købe jordskruer eller få hjælp til vurdering, dimensionering og montering. Vi tilpasser altid løsningen til konstruktionen og forholdene i jorden.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#kontakt-form"
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950 px-7 py-3.5 rounded-xl text-sm font-bold uppercase tracking-widest text-center transition-colors shadow-lg hover:shadow-amber-500/20 inline-flex items-center justify-center gap-2"
                >
                  Få et uforpligtende tilbud
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/skruer"
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-7 py-3.5 rounded-xl text-sm font-bold uppercase tracking-widest text-center transition-colors inline-flex items-center justify-center"
                >
                  Se alle skruer & specifikationer
                </Link>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-5" id="kontakt-form">
              <ContactForm
                variant="hero"
                defaultService="Jordskruer"
                title="Få tilbud på jordskruer"
                subtitle="Få en vurdering eller et uforpligtende prisoverslag til dit projekt."
              />
            </div>

          </div>
        </div>
      </section>

      {/* HIGHLIGHTS BAR */}
      <section className="bg-amber-500 text-slate-950 py-6 border-y border-amber-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center justify-center">
              <span className="text-2xl lg:text-3xl font-black">WSF Stål</span>
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-900 mt-1">Varmgalvaniseret kvalitet</span>
            </div>
            <div className="flex flex-col items-center justify-center">
              <span className="text-2xl lg:text-3xl font-black">0 Timers</span>
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-900 mt-1">Hærdetid på beton</span>
            </div>
            <div className="flex flex-col items-center justify-center">
              <span className="text-2xl lg:text-3xl font-black">100%</span>
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-900 mt-1">Bæreevnekontrol</span>
            </div>
            <div className="flex flex-col items-center justify-center">
              <span className="text-2xl lg:text-3xl font-black">Hele DK</span>
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-900 mt-1">Salg & montering</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1: HVAD ER JORDSKRUER */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-amber-600 text-xs font-bold uppercase tracking-widest mb-3">
                <HelpCircle className="w-4 h-4" />
                Produktet & Løsningen
              </div>
              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-6">
                Hvad er jordskruer, og hvordan bærer de din konstruktion?
              </h2>
              <div className="space-y-4 text-slate-600 text-base sm:text-lg leading-relaxed">
                <p>
                  En jordskrue er den fysiske stålskrue, der føres ned til et jordlag med den nødvendige bæreevne. Den overfører belastningen fra konstruktionen til jorden og bliver sammen med beslag og overbygning en del af et <Link to="/skruefundering" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">skruefundament</Link>. Jordskruen er altså produktet, mens fundamentet er hele løsningen.
                </p>
                <p>
                  Vi anvender professionelle skruer i varmgalvaniseret stål fra WSF Schroeffundering. Sortimentet omfatter forskellige diametre, længder, godstykkelser og systemdele. Det rette valg afhænger af jordbund, konstruktion og påvirkning fra tryk, træk og vandrette kræfter.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                <div className="p-4 bg-slate-50 border-l-4 border-amber-500 rounded-r-lg">
                  <h4 className="font-bold text-slate-900 text-sm uppercase">Trykkræfter</h4>
                  <p className="text-xs text-slate-600 mt-1">Optager bygningens lodrette egenvægt og nyttelast sikkert.</p>
                </div>
                <div className="p-4 bg-slate-50 border-l-4 border-amber-500 rounded-r-lg">
                  <h4 className="font-bold text-slate-900 text-sm uppercase">Trækkræfter</h4>
                  <p className="text-xs text-slate-600 mt-1">Forankrer mod vindløft og opadrettede trækpåvirkninger.</p>
                </div>
                <div className="p-4 bg-slate-50 border-l-4 border-amber-500 rounded-r-lg">
                  <h4 className="font-bold text-slate-900 text-sm uppercase">Vandrette kræfter</h4>
                  <p className="text-xs text-slate-600 mt-1">Sikrer mod sideværts stabilitet og vindtryk.</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-amber-500/10 rounded-2xl transform rotate-2"></div>
                <div className="relative bg-slate-900 rounded-xl overflow-hidden shadow-2xl p-4 sm:p-6 text-white border border-slate-800">
                  <img
                    src={screwImage}
                    alt="WSF varmgalvaniseret jordskrue med svejset flange og gevind"
                    className="w-full h-64 object-cover rounded-lg mb-6 border border-slate-700"
                  />
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                      <span className="text-xs text-slate-400 uppercase font-semibold">Materiale</span>
                      <span className="text-sm font-bold text-amber-400">Varmgalvaniseret stål</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                      <span className="text-xs text-slate-400 uppercase font-semibold">Producent</span>
                      <span className="text-sm font-bold text-white">WSF Schroeffundering</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                      <span className="text-xs text-slate-400 uppercase font-semibold">Dimensioner</span>
                      <span className="text-sm font-bold text-white">Ø60 / Ø76 / Ø89 / Ø114 mm</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400 uppercase font-semibold">Montering</span>
                      <span className="text-sm font-bold text-white">Selvbyg eller professionel</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: HVILKE JORDSKRUER PASSER TIL DIT BYGGEPROJEKT */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 text-amber-600 text-xs font-bold uppercase tracking-widest mb-3">
              <Compass className="w-4 h-4" />
              Anvendelsesområder
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-6">
              Hvilke jordskruer passer til dit byggeprojekt?
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Jordskruer bruges til alt fra mindre haveprojekter til større byggeri. Skruens mål og antal skal ikke vælges ud fra konstruktionens navn alene. Et let skur og et hus stiller vidt forskellige krav, og derfor ser vi på både belastning, placering, jordbund og den ønskede opbygning.
            </p>
          </div>

          {/* 4 Cards based on bullets */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center font-black text-xl mb-4">
                  01
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Jordskruer til skur</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Danner stabile punkter under remme og gulvkonstruktion uden et traditionelt støbt fundament.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-semibold text-amber-600">
                <CheckCircle2 className="w-4 h-4" />
                Hurtig opstart
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center font-black text-xl mb-4">
                  02
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Jordskruer til stolper</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Bruges ved hegn, overdækninger og andre konstruktioner, hvor placering og forankring er vigtig.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-semibold text-amber-600">
                <CheckCircle2 className="w-4 h-4" />
                Præcis fiksering
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center font-black text-xl mb-4">
                  03
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Jordskruer til hus</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Kræver en projekttilpasset vurdering, korrekt dimensionering og kontrol af bæreevnen under installationen.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-semibold text-amber-600">
                <CheckCircle2 className="w-4 h-4" />
                Fuld dokumentation
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center font-black text-xl mb-4">
                  04
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Jordskruer til fundament</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Indgår under <Link to="/terrasseskruer" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">terrasser</Link>, carporte, udestuer, modulbyggeri og erhvervskonstruktioner.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-semibold text-amber-600">
                <CheckCircle2 className="w-4 h-4" />
                Fleksibel løsning
              </div>
            </div>
          </div>

          {/* Deep dive sub-sections: Skur & stolper vs Hus */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-500 text-slate-950 rounded-lg">
                  <Ruler className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">
                  Jordskruer til skur og stolper gør opstarten enkel
                </h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Ved et skur kan jordskruerne placeres præcist under den bærende konstruktion, så du kan bygge videre, når monteringen er afsluttet. Til stolper vælger vi en løsning, der passer til både stolpens fastgørelse og de kræfter, konstruktionen udsættes for. Dermed undgår du et tilfældigt produktvalg.
              </p>
            </div>

            <div className="bg-slate-900 text-white p-8 rounded-2xl border border-slate-800 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-500 text-slate-950 rounded-lg">
                  <Building2 className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-black uppercase tracking-tight text-white">
                  Jordskruer til hus kræver grundig planlægning
                </h3>
              </div>
              <p className="text-slate-300 leading-relaxed">
                Ved boliger og andre krævende konstruktioner skal funderingen vurderes som en samlet del af projektet. Vi vælger diameter, længde, godstykkelse og eventuelle forlængerdele efter beregning og jordbund. Under monteringen kontrollerer vi hver skrues bæreevne, før byggeriet fortsætter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: HVORFOR VÆLGE JORDSKRUER FREM FOR BETON */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <img
                src={siteImage}
                alt="Montage af skruefundament uden omfattende gravearbejde på byggepladsen"
                className="w-full h-auto rounded-2xl shadow-xl border border-slate-200 object-cover"
              />
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-amber-600 text-xs font-bold uppercase tracking-widest mb-3">
                <TrendingUp className="w-4 h-4" />
                Fordele & Sammenligning
              </div>
              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-6">
                Hvorfor vælge jordskruer frem for et betonfundament?
              </h2>
              <div className="space-y-4 text-slate-600 text-base sm:text-lg leading-relaxed">
                <p>
                  Med jordskruer slipper du normalt for omfattende udgravning, betonblanding og ventetid på hærdning. Fundamentpunkterne etableres direkte i jorden, og den efterfølgende konstruktion kan monteres med det samme. Det kan forkorte arbejdsprocessen og begrænse behovet for håndtering af overskudsjord.
                </p>
                <p>
                  En løsning med jordskruer kan også give en mindre påvirkning af byggearealet, fordi arbejdet er koncentreret omkring fundamentpunkterne. Stålet kan genanvendes, men den samlede pris og miljøfordel afhænger altid af projektet og af den løsning, som jordskruerne sammenlignes med.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <Clock className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Ingen ventetid</h4>
                    <p className="text-xs text-slate-600 mt-1">Byg videre straks skruerne er monteret.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <Leaf className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Mindre jordhåndtering</h4>
                    <p className="text-xs text-slate-600 mt-1">Skåner haven og byggearealet optimalt.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: SÅDAN VÆLGER OG MONTERER VI JORDSKRUER */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14 text-center mx-auto">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-widest mb-4">
              <Wrench className="w-4 h-4" />
              Trin for trin proces
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white mb-6">
              Sådan vælger og monterer vi jordskruer
            </h2>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Et godt resultat begynder, før den første skrue sættes i jorden. Vi gennemgår tegninger, belastning og jordbundsforhold, så placering og dimensioner passer til projektet. Herefter afsætter vi fundamentpunkterne med laser og udfører monteringen med specialudstyr.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-slate-800/80 border border-slate-700 p-6 rounded-2xl relative">
              <div className="text-4xl font-black text-amber-500/30 mb-4">01</div>
              <h3 className="text-lg font-bold text-white mb-3">Vurdering af forhold</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi vurderer konstruktionen og de kendte forhold på byggepladsen.
              </p>
            </div>

            <div className="bg-slate-800/80 border border-slate-700 p-6 rounded-2xl relative">
              <div className="text-4xl font-black text-amber-500/30 mb-4">02</div>
              <h3 className="text-lg font-bold text-white mb-3">Dimensionering</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi dimensionerer skruer, skruehoveder og eventuelle forlængerdele.
              </p>
            </div>

            <div className="bg-slate-800/80 border border-slate-700 p-6 rounded-2xl relative">
              <div className="text-4xl font-black text-amber-500/30 mb-4">03</div>
              <h3 className="text-lg font-bold text-white mb-3">Opmåling & nedskruning</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi måler fundamentpunkterne op og skruer løsningen ned i de bærende jordlag.
              </p>
            </div>

            <div className="bg-slate-800/80 border border-slate-700 p-6 rounded-2xl relative">
              <div className="text-4xl font-black text-amber-500/30 mb-4">04</div>
              <h3 className="text-lg font-bold text-white mb-3">Bæreevnekontrol</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi kontrollerer bæreevnen under installationen og gør fundamentet klar til videre byggeri.
              </p>
            </div>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/80 rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto text-center">
            <p className="text-slate-300 leading-relaxed text-base sm:text-lg">
              Der findes ikke én standardlængde, som passer til alle projekter. Skruens størrelse og monteringsdybde skal passe til jordtypen og de belastninger, fundamentet skal optage. Derfor anbefaler vi en faglig vurdering, især når jordskruer skal bære et hus eller en anden krævende konstruktion.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5: KØB JORDSKRUER ELLER VÆLG EN FÆRDIG FUNDERINGSLØSNING */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 text-amber-600 text-xs font-bold uppercase tracking-widest mb-3">
              <ShoppingBag className="w-4 h-4" />
              Fleksible muligheder
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-6">
              Køb jordskruer, eller vælg en færdig funderingsløsning
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Du kan komme til os, hvis du søger konkrete jordskruer og systemdele, eller hvis du vil have en samlet løsning med rådgivning og professionel montering. Vi forhandler også systemer som <Link to="/groundplug" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">GroundPlug</Link> og løsninger til forankring med <Link to="/jordanker" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">jordanker</Link>. Læs mere <Link to="/om-os" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">om os</Link>. Vi hjælper med at afklare, om du selv skal stå for dele af arbejdet, eller om CM Skruefundament skal udføre funderingen fra start til slut.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="text-xl font-black uppercase tracking-tight text-slate-900 mb-4">
                Hvad påvirker prisen på jordskruer?
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Prisen på jordskruer afhænger blandt andet af antal, diameter, længde, godstykkelse, systemdele og behovet for montering. Et retvisende tilbud kræver derfor oplysninger om konstruktionen og placeringen. Vi anbefaler ikke en bestemt skrue alene ud fra en generel stykpris.
              </p>
              <ul className="space-y-2.5 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                  <span>Antal fundamentpunkter og belastningskrav</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                  <span>Skruediameter, længde og forlængerstykker</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                  <span>Beslag, flangehoveder og systemdele</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                  <span>Behov for professionel maskinmontering & dokumentation</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-900 text-white p-8 rounded-2xl border border-slate-800 shadow-lg flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-widest mb-3">
                  <MapPin className="w-4 h-4" />
                  Landsdækkende Service
                </div>
                <h3 className="text-xl font-black uppercase tracking-tight text-white mb-4">
                  Fundering i Jylland, på Fyn og Sjælland
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Vi arbejder med fundering i Jylland, på Fyn og på Sjælland for både private og erhvervskunder. Fortæl os, hvad du vil bygge, hvor projektet ligger, og hvordan konstruktionen er planlagt. Så kan vi vurdere de relevante jordskruer til fundament og udarbejde et uforpligtende tilbud.
                </p>
              </div>

              <div className="pt-6 border-t border-slate-800 flex flex-col sm:flex-row gap-4 text-sm">
                <a 
                  href="tel:+4526796139" 
                  className="flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold"
                >
                  <Phone className="w-4 h-4" />
                  +45 26 79 61 39
                </a>
                <a 
                  href="mailto:cs@cmskruefundament.dk" 
                  className="flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold"
                >
                  <Mail className="w-4 h-4" />
                  cs@cmskruefundament.dk
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <FAQSection
        badgeText="Ofte stillede spørgsmål"
        title="Spørgsmål & svar om jordskruer"
        description="Find svar på de mest gængse spørgsmål om montering, frostfri dybde, forhindringer i jorden og genanvendelse af jordskruer."
        themeColor="amber"
        bgClass="bg-slate-50"
        items={[
          {
            question: "Hvordan monteres jordskruer, og hvilket værktøj kræves der?",
            answer: `Mindre jordskruer til lette projekter som hegn, postkasser eller små træterrasser kan monteres manuelt med et simpelt drejeværktøj (T-nøgle eller momentstang) eller en kraftig slagskruemaskine med det rette adapterhoved.

Til større byggeprojekter, tilbygninger, carporte og helårsbyggeri anvender vi hydrauliske eller elektriske skruemaskiner med højt drejningsmoment, som sikrer præcis lodret nedføring og optimal komprimering af jorden omkring gevindet. Den mekaniske nedskruning sikrer samtidig en præcis justering i højde og vatter med millimeterpræcision.`
          },
          {
            question: "Hvad er forskellen på jordskruer og traditionelle nedgravede træpæle?",
            answer: `Hvor traditionelle træstolper nedgraves direkte i jorden eller støbes fast med stolpebeton, befinder jordskruens stålhoved sig over terræn, så trækonstruktionen aldrig kommer i direkte kontakt med den fugtige jord. Dette eliminerer risikoen for råd og svamp i træets bund, hvilket forlænger konstruktionens samlede levetid med mange årtier.

Samtidig slipper du for tungt gravearbejde, opgravning af havebede og bortskaffelse af overskydende jord, ligesom en jordskrue ikke skubbes op af vinterens frosthævninger.`
          },
          {
            question: "Hvor dybt skal en jordskrue skrues ned for at nå frostfri dybde?",
            answer: `I Danmark ligger den officielle frostfri dybde normalt mellem 90 og 120 cm under terrænoverfladen afhængig af de lokale jordbundsforhold og geografiske placering. For at garantere en stabil konstruktion, der ikke bevæger sig under hård vinterfrost, dimensioneres jordskruer altid således, at det bærende gevind forankres sikkert under denne frostgrænse.

Ved bløde overfladelag eller skrånende terræn kan skruerne forlænges til 1,5, 2,0 eller 3,0 meters dybde, så der opnås maksimal stabilitet i de faste underliggende jordlag.`
          },
          {
            question: "Hvad sker der, hvis man rammer store sten eller trærødder under montagen?",
            answer: `Jordskruer er udstyret med en skarp spids og et kraftigt gevind, der ubesværet kan skubbe mindre sten, grus og mindre rødder til side under nedskruningen. Rammer man en større sten eller en kraftig trærod, kan man i mange tilfælde forstanse eller forbore et styrehul med specialbor, som knuser forhindringen uden at svække jordens omkringliggende bæreevne.

Skulle en massiv kampesten blokere helt, kan jordskruen let skrues op igen, hvorefter placeringen justeres få centimeter, eller fundamentets beslag forskydes uden at ødelægge terrænet.`
          },
          {
            question: "Kan jordskruer skrues op og genbruges, hvis konstruktionen skal flyttes?",
            answer: `Ja, en af de helt unikke egenskaber ved jordskruer er deres fulde reversibilitet, hvilket gør dem særligt attraktive til både midlertidige og permanente opstillinger. Skruerne kan nemt og skånsomt skrues mod uret op af jorden ved hjælp af det samme værktøj, som blev brugt til montagen, hvorefter terrænet efterlades stort set uberørt uden betonrester i undergrunden.

De opdragede jordskruer bevarer deres fulde styrke og galvanisering og kan umiddelbart genanvendes til et nyt byggeprojekt eller genanvendes som skrotstål.`
          }
        ]}
      />

      {/* CONTACT FORM SECTION */}
      <section id="kontakt" className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-amber-600 text-xs font-bold uppercase tracking-widest mb-3">
              <Mail className="w-4 h-4" />
              Kontakt & Rådgivning
            </div>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900">
              Få et uforpligtende tilbud på jordskruer
            </h2>
            <p className="text-slate-600 mt-3 text-lg">
              Send os gerne tegninger, mål eller beskrivelse af dit byggeprojekt. Vi vender hurtigt tilbage med faglig sparring og et uforpligtende tilbud.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm">
            <ContactForm defaultService="Jordskruer" />
          </div>
        </div>
      </section>
    </Layout>
  );
}
